import React, { useState, useEffect, useMemo } from 'react';
import { Play, Download, FileText, MonitorPlay, Menu, CheckCircle, Circle, Archive, FileSpreadsheet, Search, X, TrendingUp } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from 'sonner';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import axios from 'axios';
import AdminLoginModal from './AdminLoginModal';
import PdfThumbnail from './PdfThumbnail';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const [videos, setVideos] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [watchedVideos, setWatchedVideos] = useState([]);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [loading, setLoading] = useState(true);

  // Search & filter state
  const [videoQuery, setVideoQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [docQuery, setDocQuery] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [vidsRes, docsRes] = await Promise.all([
           axios.get(`${BACKEND_URL}/api/videos`),
           axios.get(`${BACKEND_URL}/api/documents`)
        ]);
        setVideos(vidsRes.data);
        setDocuments(docsRes.data);
      } catch (err) {
        console.error("Error fetching data", err);
        toast.error("Error cargando contenido");
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    const saved = localStorage.getItem('rcdm_watched_videos');
    if (saved) {
      setWatchedVideos(JSON.parse(saved));
    }
  }, []);

  const toggleWatched = (videoId) => {
    let newWatched;
    if (watchedVideos.includes(videoId)) {
      newWatched = watchedVideos.filter(id => id !== videoId);
      toast.info("Marcado como pendiente");
    } else {
      newWatched = [...watchedVideos, videoId];
      toast.success("Marcado como completado");
    }
    setWatchedVideos(newWatched);
    localStorage.setItem('rcdm_watched_videos', JSON.stringify(newWatched));
  };

  const getProgress = () => {
    if (videos.length === 0) return 0;
    return Math.round((watchedVideos.length / videos.length) * 100);
  };

  const getDownloadUrl = (docUrl) => {
      if (docUrl.startsWith('/')) {
          return `${BACKEND_URL}${docUrl}`;
      }
      return docUrl;
  };

  const trackDownload = async (doc) => {
      // Optimistic update for snappy UX
      setDocuments(prev => prev.map(d => d.id === doc.id ? { ...d, download_count: (d.download_count || 0) + 1 } : d));
      try {
          const res = await axios.post(`${BACKEND_URL}/api/documents/${doc.id}/track-download`);
          // Reconcile with server value
          setDocuments(prev => prev.map(d => d.id === doc.id ? { ...d, download_count: res.data.download_count } : d));
      } catch (err) {
          console.error("Track download failed", err);
      }
  };

  const getVimeoSrc = (video) => {
      let vimeoId = video.vimeoId || "";
      let hash = video.hash || "";

      if (vimeoId.includes('vimeo.com')) {
          try {
              const url = new URL(vimeoId);
              const pathParts = url.pathname.split('/').filter(p => p);
              if (pathParts.length >= 1 && /^\d+$/.test(pathParts[0])) {
                  vimeoId = pathParts[0];
                  if (pathParts.length >= 2) hash = pathParts[1];
              }
          } catch(e) {}
      }

      let src = `https://player.vimeo.com/video/${vimeoId}`;
      if (hash) src += `?h=${hash}`;
      src += hash ? '&' : '?';
      src += 'title=0&byline=0&portrait=0';
      return src;
  };

  // Derived: categories list
  const categories = useMemo(() => {
      const set = new Set(videos.map(v => v.category).filter(Boolean));
      return ['all', ...Array.from(set)];
  }, [videos]);

  // Derived: filtered lists
  const filteredVideos = useMemo(() => {
      const q = videoQuery.trim().toLowerCase();
      return videos.filter(v => {
          const matchCat = activeCategory === 'all' || v.category === activeCategory;
          const matchQ = !q || (v.title || '').toLowerCase().includes(q) || (v.category || '').toLowerCase().includes(q);
          return matchCat && matchQ;
      });
  }, [videos, videoQuery, activeCategory]);

  const filteredDocs = useMemo(() => {
      const q = docQuery.trim().toLowerCase();
      if (!q) return documents;
      return documents.filter(d =>
          (d.title || '').toLowerCase().includes(q) ||
          (d.type || '').toLowerCase().includes(q)
      );
  }, [documents, docQuery]);

  const handleDownloadAll = async () => {
    setDownloadingZip(true);
    toast.loading("Preparando descarga...", { id: "zip-download" });

    try {
        const zip = new JSZip();
        const targets = filteredDocs.length ? filteredDocs : documents;

        const promises = targets.map(async (doc) => {
            try {
                const proxyUrl = `${BACKEND_URL}/api/proxy-download?url=${encodeURIComponent(doc.url)}`;
                const response = await axios.get(proxyUrl, { responseType: 'blob' });

                const extension = doc.type.toLowerCase();
                const filename = `${doc.title.replace(/[^a-z0-9]/gi, '_')}.${extension}`;
                zip.file(filename, response.data);
                trackDownload(doc);
            } catch (err) {
                console.error(`Failed to fetch ${doc.title}`, err);
                toast.error(`No se pudo incluir ${doc.title} (Error de Red)`);
            }
        });

        await Promise.all(promises);

        const content = await zip.generateAsync({ type: "blob" });
        saveAs(content, "RCDM_Documentacion.zip");
        toast.success("¡Descarga iniciada!", { id: "zip-download" });
    } catch (error) {
        console.error("Zip generation failed", error);
        toast.error("Error al generar el archivo Zip", { id: "zip-download" });
    } finally {
        setDownloadingZip(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#09090B] flex items-center justify-center text-white">Cargando...</div>;

  return (
    <div className="min-h-screen bg-[#09090B] pb-20">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-panel border-b border-white/5">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
           <div className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_ab97cb40-047d-4dfe-8f46-5d5753921605/artifacts/kpt72b31_escudo.png"
                alt="RCDM Logo"
                className="h-10 w-10 drop-shadow-md"
              />
              <span className="text-xl font-bold tracking-tight uppercase text-white hidden sm:block">SOP RCDM</span>
           </div>

           <div className="flex items-center gap-4">
              <AdminLoginModal />

              <div className="flex flex-col items-end mr-2">
                 <span className="text-xs text-zinc-400">Progreso</span>
                 <span className="text-sm font-bold text-red-500">{getProgress()}%</span>
              </div>
              <div className="h-8 w-8 rounded-full bg-red-600 flex items-center justify-center text-white font-bold text-xs">S</div>
           </div>
        </div>
        <div className="h-1 w-full bg-zinc-900">
            <div
                className="h-full bg-red-600 transition-all duration-500 ease-out shadow-[0_0_10px_rgba(220,38,38,0.5)]"
                style={{ width: `${getProgress()}%` }}
            ></div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-12 px-4 overflow-hidden">
         <div className="absolute inset-0 bg-gradient-to-b from-red-900/10 to-[#09090B] z-0"></div>
         <div className="container mx-auto relative z-10 max-w-4xl text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 uppercase tracking-tight">
               Bienvenido a la plataforma <span className="text-red-600">SOP RCDM</span>
            </h1>
            <p className="text-zinc-300 text-lg md:text-xl leading-relaxed max-w-3xl mx-auto">
               Este espacio está diseñado para acompañarte en tu desarrollo profesional, reforzar los estándares de servicio y asegurar que cada experiencia ofrecida a nuestros aficionados, clientes y partners sea excelente.
            </p>
         </div>
      </section>

      <div className="container mx-auto px-4 space-y-16">
        {/* Videos Section */}
        <section>
           <div className="flex flex-col gap-6 mb-8">
               <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                     <MonitorPlay className="text-red-600 h-6 w-6" />
                     <h2 className="text-3xl font-bold uppercase text-white">Módulos de Video</h2>
                  </div>
                  <span className="text-zinc-500 text-sm hidden sm:inline-block" data-testid="videos-progress-label">
                     {watchedVideos.length} / {videos.length} Completados
                  </span>
               </div>

               {/* Search + Category Filter */}
               <div className="flex flex-col md:flex-row gap-3">
                   <div className="relative flex-1">
                       <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                       <Input
                           placeholder="Buscar por título o categoría..."
                           value={videoQuery}
                           onChange={(e) => setVideoQuery(e.target.value)}
                           className="pl-9 pr-9 bg-zinc-900/60 border-zinc-800 text-white placeholder:text-zinc-500 focus-visible:ring-red-600"
                           data-testid="video-search-input"
                       />
                       {videoQuery && (
                           <button
                               onClick={() => setVideoQuery('')}
                               className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                               data-testid="video-search-clear"
                               aria-label="Limpiar búsqueda"
                           >
                               <X className="h-4 w-4" />
                           </button>
                       )}
                   </div>
                   <div className="flex gap-2 overflow-x-auto pb-1">
                       {categories.map((cat) => (
                           <button
                               key={cat}
                               onClick={() => setActiveCategory(cat)}
                               className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide border transition-all ${
                                   activeCategory === cat
                                       ? 'bg-red-600 border-red-600 text-white shadow-[0_0_12px_rgba(220,38,38,0.4)]'
                                       : 'bg-transparent border-zinc-800 text-zinc-400 hover:border-red-600/60 hover:text-white'
                               }`}
                               data-testid={`category-filter-${cat}`}
                           >
                               {cat === 'all' ? 'Todas' : cat}
                           </button>
                       ))}
                   </div>
               </div>
           </div>

           {filteredVideos.length === 0 ? (
               <div className="glass-panel rounded-lg p-12 text-center text-zinc-500" data-testid="videos-empty-state">
                   <Search className="h-8 w-8 mx-auto mb-3 opacity-40" />
                   <p>No se encontraron videos con esos criterios.</p>
               </div>
           ) : (
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredVideos.map((video) => {
                     const isWatched = watchedVideos.includes(video.id);

                     return (
                         <div key={video.id} className={`group glass-panel rounded-lg overflow-hidden transition-all duration-300 ${isWatched ? 'opacity-80 hover:opacity-100 border-green-900/30' : 'hover-glow'}`}>
                            <div className="relative aspect-video bg-black">
                               <iframe
                                  src={getVimeoSrc(video)}
                                  className="absolute top-0 left-0 w-full h-full"
                                  frameBorder="0"
                                  allow="autoplay; fullscreen; picture-in-picture"
                                  allowFullScreen
                                  title={video.title}
                               ></iframe>

                               {isWatched && (
                                   <div className="absolute top-2 right-2 z-10 bg-black/70 backdrop-blur-md px-2 py-1 rounded flex items-center gap-1 border border-green-500/30">
                                       <CheckCircle className="w-3 h-3 text-green-500" />
                                       <span className="text-[10px] uppercase font-bold text-green-500 tracking-wide">Visto</span>
                                   </div>
                               )}
                            </div>
                            <div className="p-5">
                               <div className="flex justify-between items-start mb-2">
                                   <Badge className="bg-red-600/20 text-red-500 hover:bg-red-600/30 border-none">
                                      {video.category}
                                   </Badge>

                                   <button
                                      onClick={() => toggleWatched(video.id)}
                                      className={`text-xs flex items-center gap-1 px-2 py-1 rounded transition-colors ${isWatched ? 'text-green-500 hover:text-green-400' : 'text-zinc-500 hover:text-white'}`}
                                      data-testid={`toggle-watched-${video.id}`}
                                   >
                                      {isWatched ? (
                                          <><CheckCircle className="w-4 h-4" /> Completado</>
                                      ) : (
                                          <><Circle className="w-4 h-4" /> Marcar visto</>
                                      )}
                                   </button>
                               </div>

                               <h3 className={`text-xl font-bold mb-1 transition-colors ${isWatched ? 'text-zinc-400' : 'text-white group-hover:text-red-500'}`}>
                                  {video.title}
                               </h3>

                               <div className="flex items-center text-xs text-zinc-500 mt-3">
                                  <Play className="h-3 w-3 mr-1 fill-current" /> Streaming Disponible
                               </div>
                            </div>
                         </div>
                     );
                  })}
               </div>
           )}
        </section>

        {/* Documents Section */}
        <section>
            <div className="flex flex-col gap-6 mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                   <div className="flex items-center gap-3">
                      <FileText className="text-red-600 h-6 w-6" />
                      <h2 className="text-3xl font-bold uppercase text-white">Documentación</h2>
                   </div>

                   <Button
                        onClick={handleDownloadAll}
                        disabled={downloadingZip}
                        className="bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700"
                        data-testid="download-all-zip"
                   >
                       {downloadingZip ? (
                           <>Generando Zip...</>
                       ) : (
                           <><Archive className="mr-2 h-4 w-4" /> Descargar Todo (Zip)</>
                       )}
                   </Button>
               </div>

               <div className="relative max-w-xl">
                   <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                   <Input
                       placeholder="Buscar documento por nombre o tipo..."
                       value={docQuery}
                       onChange={(e) => setDocQuery(e.target.value)}
                       className="pl-9 pr-9 bg-zinc-900/60 border-zinc-800 text-white placeholder:text-zinc-500 focus-visible:ring-red-600"
                       data-testid="doc-search-input"
                   />
                   {docQuery && (
                       <button
                           onClick={() => setDocQuery('')}
                           className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                           data-testid="doc-search-clear"
                           aria-label="Limpiar búsqueda"
                       >
                           <X className="h-4 w-4" />
                       </button>
                   )}
               </div>
           </div>

           {filteredDocs.length === 0 ? (
               <div className="glass-panel rounded-lg p-12 text-center text-zinc-500" data-testid="docs-empty-state">
                   <Search className="h-8 w-8 mx-auto mb-3 opacity-40" />
                   <p>No se encontraron documentos con ese criterio.</p>
               </div>
           ) : (
               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {filteredDocs.map((doc) => (
                     <div key={doc.id} className="glass-panel p-6 rounded-lg flex items-start gap-4 hover:border-red-600/50 transition-colors group" data-testid={`doc-card-${doc.id}`}>
                        <div className={`h-20 w-20 sm:h-24 sm:w-24 rounded overflow-hidden shrink-0 ring-1 ${doc.type === 'XLSX' ? 'ring-green-600/40 bg-green-900/20' : 'ring-red-600/40'} group-hover:ring-2 transition-all`}>
                           {doc.type === 'XLSX' ? (
                               <div className="w-full h-full flex items-center justify-center">
                                  <FileSpreadsheet className="h-10 w-10 text-green-500" />
                               </div>
                           ) : (
                               <PdfThumbnail docId={doc.id} url={doc.url} alt={doc.title} />
                           )}
                        </div>
                        <div className="flex-1 min-w-0">
                           <h3 className="text-lg font-bold text-white mb-1 truncate">{doc.title}</h3>
                           <div className="flex items-center gap-3 text-xs text-zinc-500 mb-4 flex-wrap">
                              <span>Documento Oficial • {doc.type}</span>
                              <span className="inline-flex items-center gap-1 text-zinc-400" data-testid={`doc-download-count-${doc.id}`}>
                                 <TrendingUp className="h-3 w-3" />
                                 {(doc.download_count || 0)} descarga{(doc.download_count || 0) === 1 ? '' : 's'}
                              </span>
                           </div>
                           <Button
                              asChild
                              variant="secondary"
                              size="sm"
                              className={`bg-zinc-800 border-none transition-all ${doc.type === 'XLSX' ? 'hover:bg-green-600' : 'hover:bg-red-600'} hover:text-white`}
                              data-testid={`download-btn-${doc.id}`}
                            >
                              <a
                                  href={getDownloadUrl(doc.url)}
                                  download
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() => trackDownload(doc)}
                              >
                                 <Download className="mr-2 h-4 w-4" /> Descargar {doc.type}
                              </a>
                           </Button>
                        </div>
                     </div>
                  ))}
               </div>
           )}
        </section>
      </div>

      {/* Footer */}
      <footer className="mt-20 border-t border-white/5 py-12 bg-black/40">
         <div className="container mx-auto px-4 text-center">
            <img
                src="https://customer-assets.emergentagent.com/job_ab97cb40-047d-4dfe-8f46-5d5753921605/artifacts/kpt72b31_escudo.png"
                alt="RCDM Logo"
                className="h-12 w-12 mx-auto mb-4 opacity-50 grayscale hover:grayscale-0 transition-all"
            />
            <p className="text-zinc-600 text-sm">
               © {new Date().getFullYear()} GDI. Plataforma de Formación RCDM. Solo Uso Interno.
            </p>
         </div>
      </footer>
    </div>
  );
}
